import { Settings2, X } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Tag chips.
 *
 * The colour comes from a palette key, so the same tag is legible in both
 * themes without the operator choosing twice. The name is always shown: a row
 * of coloured dots is a memory test, and ten tags in is where it stops working.
 */

export interface TagLike {
  readonly id: string;
  readonly name: string;
  readonly color: string;
}

export function TagChip({
  tag,
  size = 'md',
  onRemove,
  onClick,
  onEdit,
  selected = false,
  count,
}: {
  readonly tag: TagLike;
  readonly size?: 'sm' | 'md';
  /** Shows a remove control inside the chip. For "tags on this site". */
  readonly onRemove?: () => void;
  /** Makes the chip a toggle. For the filter bar. */
  readonly onClick?: () => void;
  /** Shows a settings control, revealed on hover and on keyboard focus. */
  readonly onEdit?: () => void;
  readonly selected?: boolean;
  readonly count?: number;
}): ReactNode {
  const classes = [
    'tag-chip',
    `tag-${tag.color}`,
    size === 'sm' ? 'tag-chip-sm' : '',
    selected ? 'tag-chip-selected' : '',
    onClick ? 'tag-chip-button' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      <span className="tag-dot" aria-hidden="true" />
      <span className="tag-name">{tag.name}</span>
      {count !== undefined ? <span className="tag-count">{count}</span> : null}
    </>
  );

  // The toggle and the settings control are siblings, not nested: a button
  // inside a button is invalid and behaves differently in every browser.
  const trailing = onEdit ? (
    <button
      type="button"
      className="tag-action"
      aria-label={`Настроить тег ${tag.name}`}
      title="Настроить"
      onClick={onEdit}
    >
      <Settings2 size={12} aria-hidden="true" />
    </button>
  ) : onRemove ? (
    <button type="button" className="tag-action" aria-label={`Убрать тег ${tag.name}`} onClick={onRemove}>
      <X size={11} aria-hidden="true" />
    </button>
  ) : null;

  if (onClick) {
    return (
      <span className={`tag-group tag-${tag.color}`}>
        <button type="button" className={classes} aria-pressed={selected} onClick={onClick}>
          {body}
        </button>
        {trailing}
      </span>
    );
  }

  return (
    <span className={classes}>
      {body}
      {trailing}
    </span>
  );
}

/** The colour picker from the tag editor: one swatch per palette key. */
export function TagColorPicker({
  value,
  colors,
  onChange,
}: {
  readonly value: string;
  readonly colors: readonly string[];
  readonly onChange: (color: string) => void;
}): ReactNode {
  return (
    <div className="swatches" role="radiogroup" aria-label="Цвет тега">
      {colors.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={color === value}
          aria-label={color}
          className={`swatch tag-${color} ${color === value ? 'swatch-selected' : ''}`}
          onClick={() => onChange(color)}
        />
      ))}
    </div>
  );
}
